import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BaseParamsService } from '@app/core/base-params.service';
import { environment } from '@env/environment';
import { SessionApiProvider } from '@app/core/session/session-api-provider';
import { EmbedTokenResponse } from '@app/core/session/interfaces';
import { WithRedirectUrl } from '@app/core/_models/redirectUrl';
import { isNetworkFailure } from '@app/core/session/is-network-failure';

@Injectable()
export class SessionOidcApiService implements SessionApiProvider {
  private http = inject(HttpClient);
  private baseParamsService = inject(BaseParamsService);


  public startOidcSignIn(): Observable<string> {
    const params: Record<string, any> = {};
    const language = this.baseParamsService.language;
    if (language) {
      params.language = language;
    }

    return this.http.post<WithRedirectUrl>(
      `${this.getOidcSessionUrl()}`,
      {},
      { params },
    ).pipe(
      map(({ redirect_url }: WithRedirectUrl) => redirect_url),
    );
  }

  public signOut(): Observable<unknown> {
    return this.http.delete<WithRedirectUrl>(
      `${this.getOidcSessionUrl()}`,
    ).pipe(
      map(({ redirect_url }: WithRedirectUrl) => redirect_url),
      switchMap(url => this.http.get(url)),
    );
  }

  public authenticateBasically(): Observable<boolean> {
    return this.http.get<{ status: string }>(
      `${this.getOidcSessionUrl()}`,
    ).pipe(
      tap(() => {
        // x-authtoken, which is located in localstorage, might be left without any TTL there in some circumstances
        // if it's set, then it will be prioritized for all requests, which may return session errors
        // when oidc_session returns 200 (session cookie exists) then we must make sure that x-authtoken is cleaned up
        this.baseParamsService.removeAuthToken();
      }),
      map(() => true),
      catchError((error: unknown) => isNetworkFailure(error) ? throwError(() => error) : of(false)),
    );
  }

  public getGamesEmbedToken(): Observable<string> {
    return this.http.get<EmbedTokenResponse>(
      `${this.getOidcSessionUrl()}/embed_tokens/games_api`,
    ).pipe(
      map(({ token }: EmbedTokenResponse) => token),
    );
  }

  private getOidcSessionUrl(): string {
    return `${this.getBaseUrl()}/oidc_sessions`;
  }

  private getBaseUrl(): string {
    return `${environment.webforms_api}/v2/${this.currentSlug()}`;
  }

  private currentSlug(): string {
    return this.baseParamsService.slug;
  }
}
